import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useLogger, useTranslation, useFormat, createError} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {
    ExpenseListForApprovalResource,
    CreateBatchForSeveralExpensesRequest,
    DeleteExpenseBatchRequest,
    ListAllExpensesInBatchRequest,
    ExpenseBatchSummaryResource,
    ApproveABatchOfExpensesRequest,
    SendExpensesToPunchoutOperationRequest,
} from '@api';
import type {FormattedExpenseTableItem} from '../types';
import {clientExpenseEndpoints} from '@/registry';

export const useExpenseValidationStore = defineStore('expense-validation', () => {
    const logger = useLogger();

    const {t} = useTranslation();

    const {formatAmount, formatDate} = useFormat();

    // fetched expenses + price summary + PO number
    const expensesList = ref<ExpenseListForApprovalResource>(null!);
    const priceSummary = computed(() => batchExpenseItem.value?.summary ?? expensesList.value?.summary);
    const clientPurchaseOrder = computed(() => expensesList.value?.clientPurchaseOrder);
    const clientPurchaseOrderRequired = computed(() => expensesList.value?.clientPurchaseOrderRequired);
    // fetched expense batch infos
    const batchExpenseItem = ref<ExpenseBatchSummaryResource>(null!);
    const tempBatchId = ref<string>(null!);

    // loading state
    const loadingExpensesList = ref(false);

    async function blockExpenseApproval(expenseId: string, projectId: string, message: string) {
        try {
            await clientExpenseEndpoints.blockExpenseApproval({expenseId, projectId, blockExpenseApprovalRequest: {message}});
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
        }
    }

    async function unblockExpenseApproval(expenseId: string, projectId: string) {
        try {
            await clientExpenseEndpoints.unblockExpenseApproval({expenseId, projectId});
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
        }
    }

    async function fetchExpensesSubmitted(projectId: string) {
        try {
            loadingExpensesList.value = true;
            expensesList.value = await clientExpenseEndpoints.getExpensesForApproval({projectId});
        } catch (e) {
            logger.error(e);
            expensesList.value = null as unknown as ExpenseListForApprovalResource;
        } finally {
            loadingExpensesList.value = false;
        }
    }

    async function createExpenseBatch(requestObj: CreateBatchForSeveralExpensesRequest) {
        try {
            const response = await clientExpenseEndpoints.createBatchForSeveralExpenses(requestObj);
            tempBatchId.value = response.expenseBatchId;
            await fetchExpenseBatch({
                projectId: requestObj.projectId,
                expenseBatchId: response.expenseBatchId,
            });
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
            throw createError({
                statusCode: 400,
                message: 'Expense batch creation failed',
            });
        }
    }

    async function abortCreateExpenseBatch(requestObj: DeleteExpenseBatchRequest) {
        try {
            await clientExpenseEndpoints.deleteExpenseBatch(requestObj);
            await fetchExpensesSubmitted(requestObj.projectId);
        } catch (e) {
            logger.error(e);
            throw createError({
                statusCode: 400,
                message: 'Expense batch deletion failed',
            });
        }
    }

    async function fetchExpenseBatch(requestObj: ListAllExpensesInBatchRequest) {
        try {
            batchExpenseItem.value = await clientExpenseEndpoints.listAllExpensesInBatch(requestObj);
        } catch (e) {
            logger.error(e);
            batchExpenseItem.value = null as unknown as ExpenseBatchSummaryResource;
            throw createError({
                statusCode: 400,
                message: 'Expense batch not found',
            });
        }
    }

    async function validateExpensesAsBatch(requestObj: ApproveABatchOfExpensesRequest) {
        try {
            await clientExpenseEndpoints.approveABatchOfExpenses(requestObj);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.expenses.validation.success.header'),
                    message: t('projectclient.expenses.validation.success.message', [batchExpenseItem.value.batchCounterInProject]),
                    level: 'success',
                },
            });
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('projectclient.expenses.validation.error'), level: 'error'}});
            throw createError({
                statusCode: 400,
                message: 'Expense batch validation failed',
            });
        }
    }

    async function sendExpensesToPunchout(requestObj: SendExpensesToPunchoutOperationRequest) {
        try {
            const response = await clientExpenseEndpoints.sendExpensesToPunchout(requestObj);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.expenses.validation.success.header'),
                    message: t('projectclient.expenses.validation.success.message', [batchExpenseItem.value.batchCounterInProject]),
                    level: 'success',
                },
            });
            return response.redirectionUrl;
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('projectclient.expenses.validation.error'), level: 'error'}});
            throw createError({
                statusCode: 400,
                message: 'Expense batch validation failed',
            });
        }
    }

    const formattedExpensesList = computed<FormattedExpenseTableItem[]>(() => {
        return (
            expensesList.value?.expenses.map((expense) => {
                return {
                    id: expense.expenseId,
                    type: expense.category,
                    // total = amount (exlc. tax) to be in synch with expense infos on side panel:
                    total: expense.amountWithTaxesMinusVatDeductible
                        ? formatAmount(expense.currency, expense.amountWithTaxesMinusVatDeductible, {decimals: 2})
                        : '-',
                    date: expense.submissionDate ? formatDate(expense.submissionDate) : '-',
                    action: expense.receiptDownloadUrl ? 'DOWNLOAD_RECEIPT' : '-',
                    receiptUrl: expense.receiptDownloadUrl,
                    supplier: expense.supplier,
                    description: expense.description || '',
                } satisfies FormattedExpenseTableItem;
            }) ?? []
        );
    });

    return {
        fetchExpensesSubmitted,
        formattedExpensesList,
        priceSummary,
        validateExpensesAsBatch,
        clientPurchaseOrder,
        clientPurchaseOrderRequired,
        createExpenseBatch,
        abortCreateExpenseBatch,
        fetchExpenseBatch,
        batchExpenseItem,
        tempBatchId,
        sendExpensesToPunchout,
        blockExpenseApproval,
        unblockExpenseApproval,
    };
});
